import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SelkirkIcon from './icons/SelkirkIcon';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import { createPortal } from 'react-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Table from './ui/Table';
import Spinner from './ui/Spinner';
import DrillHoleMap, { PROGRESS_RING_METRICS } from './DrillHoleMap';
import DrillHole3DViewer from './DrillHole3DViewer';
import PopoutWindow from './PopoutWindow';
import CoringActivity from './CoringActivity';
import CoringDataExplorer from './CoringDataExplorer';
import CorePhotosGallery from './CorePhotosGallery';
import PlannedDrillingMap from './PlannedDrillingMap';
import { drillingService } from '../services/drillingService';
import { computeCoringDateParams } from '../utils/coringActivityHelpers';
import { plannedHoleHasLatLon } from '../utils/plannedDrillingCoords';
import { formatPlannedScheduleLabel, estimatedCompleteIso } from '../utils/plannedDrillingSchedule';
import { pickLatestProjectFromSources } from '../utils/drillingCampaignHelpers';
import {
  DASHBOARD_DATA_SOURCES,
  DRILLZ_CAMPAIGN_PROGRESS_VIEW,
  resolveDrillzMetrics,
  sourceTooltip,
} from '../utils/drillzCampaignProgress';
import { ZONE_COLORS } from '../utils/dailyReport/drillingMapForReport';
import { downloadDrillHoleMapPng } from '../utils/dailyReport/captureDrillHoleMap';
import './DrillingDashboard.css';
import './PlannedDrillingMap.css';

/** Program drilled/logged progress bars vs target (meters); used when project has no `target` in registry. */
const DEFAULT_PROGRAM_TARGET_M = 50000;

/** Toggle planned drilling map tab (hidden until ready for production). */
const SHOW_PLANNED_DRILLING_TAB = false;

function parseTargetDepthMeters(raw) {
  if (raw == null || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function isHoleSetupValuePresent(val) {
  if (val == null || val === '') return false;
  return true;
}

function formatHoleTraceCoord26908(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  return String(val);
}

function formatHoleMasterLensM(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(val);
}

/** ISO date yyyy-mm-dd from local midnight calendar day */
function isoCalendarDate(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Add whole days from a calendar base (local) returned as yyyy-mm-dd */
function calendarDatePlusDays(dateObj, deltaDays) {
  const x = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  x.setDate(x.getDate() + deltaDays);
  return isoCalendarDate(x);
}

function PlannedDetailKv({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{ fontSize: '0.66rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {label}
      </div>
      <div style={{ fontSize: '0.82rem', color: '#e5e7eb', wordBreak: 'break-word' }}>{String(value)}</div>
    </div>
  );
}

// Color palette for drill rigs
const RIG_COLORS = [
  '#3b82f6', // blue
  '#f97316', // orange
  '#22c55e', // green
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#eab308', // yellow
  '#ef4444', // red
];

const DrillingDashboard = ({ user }) => {
  const [dashboardData, setDashboardData] = useState([]);
  /** Per-project sample metrics + aggregated row for 'All' (mv_sample_progress; join key source_project_id). */
  const [sampleProgressBundle, setSampleProgressBundle] = useState(null);
  /** Active campaigns from drilling_scmi.project_sources (fallback: distinct project_id on hole rows). */
  const [projectSources, setProjectSources] = useState([]);
  /** Rows from tenant_data.v_drillz_campaign_progress (null when unavailable). */
  const [drillzCampaignRows, setDrillzCampaignRows] = useState(null);
  /** 'all' | project_id — filters map, table, coring hole list, and header sample metrics. */
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  /** After project_sources loads, select default project tab once (highest priority). */
  const initialCampaignTabAppliedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter type: 'status' | 'zone' | 'rig'
  const [filterType, setFilterType] = useState('status');
  
  const [statusFilters, setStatusFilters] = useState({
    'Complete': true,
    'In Progress': true,
    'Planned': false,
    'Lost': true
  });
  const [zoneFilters, setZoneFilters] = useState({});
  const [rigFilters, setRigFilters] = useState({});
  const [progressRingVisibility, setProgressRingVisibility] = useState(() =>
    Object.fromEntries(PROGRESS_RING_METRICS.map((m) => [m.field, true]))
  );
  /** Bottom card: full program metrics table vs hole-scoped core photos API. */
  const [bottomPanelSegment, setBottomPanelSegment] = useState('program');
  const [corePhotosPhotos, setCorePhotosPhotos] = useState([]);
  const [corePhotosLoading, setCorePhotosLoading] = useState(false);
  const [corePhotosError, setCorePhotosError] = useState(null);
  const [yearFilter, setYearFilter] = useState('all'); // 'all', '2025', '2026', etc.
  
  const [sortField, setSortField] = useState('hole_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHoleId, setSelectedHoleId] = useState(null);
  /** Bumped on Leaflet map hole pick so 3D viewer frames that trace when data is ready. */
  const [focusTraceNonce, setFocusTraceNonce] = useState(0);
  const [selectedHole, setSelectedHole] = useState(null);
  const [targetDepth, setTargetDepth] = useState(DEFAULT_PROGRAM_TARGET_M);
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [show3DView, setShow3DView] = useState(false);
  const [viewer3DPoppedOut, setViewer3DPoppedOut] = useState(false);
  /** DOM roots for a single portaled 3D viewer (preserves WebGL + all viewer state across dock/popout). */
  const [dock3dMountEl, setDock3dMountEl] = useState(null);
  const [popout3dMountEl, setPopout3dMountEl] = useState(null);
  const [activeTab, setActiveTab] = useState('visualization'); // 'visualization' | 'planned' | 'coring' | 'coring-data'

  const [plannedDateFrom, setPlannedDateFrom] = useState(() => isoCalendarDate(new Date()));
  const [plannedDateTo, setPlannedDateTo] = useState(() => calendarDatePlusDays(new Date(), 14));
  const [plannedDrillingRows, setPlannedDrillingRows] = useState([]);
  const [plannedDrillingLoading, setPlannedDrillingLoading] = useState(false);
  const [plannedDrillingError, setPlannedDrillingError] = useState(null);
  const [plannedMapSelection, setPlannedMapSelection] = useState(null);

  // Shared coring API data (Coring Activity + Coring Data Explorer)
  const [coringDateRange, setCoringDateRange] = useState('month');
  const [coringCustomStart, setCoringCustomStart] = useState('');
  const [coringCustomEnd, setCoringCustomEnd] = useState('');
  const [coringSearchTerm, setCoringSearchTerm] = useState('');
  const [coringData, setCoringData] = useState([]);
  const [coringLoading, setCoringLoading] = useState(true);
  const [coringError, setCoringError] = useState(null);

  const coringDateParams = useMemo(
    () => computeCoringDateParams(coringDateRange, coringCustomStart, coringCustomEnd),
    [coringDateRange, coringCustomStart, coringCustomEnd]
  );

  useEffect(() => {
    if (initialCampaignTabAppliedRef.current) return;
    if (!projectSources.length) return;
    initialCampaignTabAppliedRef.current = true;
    const latest = pickLatestProjectFromSources(projectSources);
    const id = latest?.project_id;
    if (id != null && String(id) !== '') {
      setSelectedProjectId(String(id));
    }
  }, [projectSources]);

  useEffect(() => {
    let cancelled = false;
    const loadCoring = async () => {
      setCoringLoading(true);
      setCoringError(null);
      try {
        const activityData = await drillingService.getCoringActivity({
          ...coringDateParams,
          billable: true,
          fetchAll: true
        });
        if (!cancelled) {
          setCoringData(activityData);
        }
      } catch (err) {
        if (!cancelled) {
          setCoringError(err.message || 'Failed to load coring activity');
          setCoringData([]);
        }
      } finally {
        if (!cancelled) {
          setCoringLoading(false);
        }
      }
    };
    loadCoring();
    return () => {
      cancelled = true;
    };
  }, [coringDateParams]);

  useEffect(() => {
    if (!show3DView) {
      setViewer3DPoppedOut(false);
    }
  }, [show3DView]);

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => ({ ...prev, [status]: !prev[status] }));
  };
  
  const toggleZoneFilter = (zone) => {
    setZoneFilters(prev => ({ ...prev, [zone]: !prev[zone] }));
  };
  
  const toggleRigFilter = (rig) => {
    setRigFilters(prev => ({ ...prev, [rig]: !prev[rig] }));
  };

  const toggleProgressRingMetric = (field) => {
    setProgressRingVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    if (!selectedHoleId) {
      setBottomPanelSegment('program');
      setCorePhotosPhotos([]);
      setCorePhotosError(null);
      return;
    }
    setBottomPanelSegment('core_photos');
  }, [selectedHoleId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedHoleId || bottomPanelSegment !== 'core_photos') {
      return undefined;
    }
    setCorePhotosLoading(true);
    setCorePhotosError(null);
    drillingService
      .getCorePhotos(selectedHoleId, {
        includeRenditions: true,
        includeSas: true,
        limit: 2500,
      })
      .then((res) => {
        if (!cancelled) setCorePhotosPhotos(res.photos || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setCorePhotosPhotos([]);
          setCorePhotosError(err.message || String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setCorePhotosLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedHoleId, bottomPanelSegment]);
  
  // Helper to extract year from hole_id (e.g., "25SCM001" -> "2025", "26SCM001" -> "2026")
  const getYearFromHoleId = (holeId) => {
    if (!holeId) return null;
    const prefix = holeId.substring(0, 2);
    if (prefix === '25') return '2025';
    if (prefix === '26') return '2026';
    if (prefix === '24') return '2024';
    if (prefix === '23') return '2023';
    return null;
  };

  /** Holes for the selected campaign tab. Rows must include project_id (same PK as drilling_scmi.project_sources). */
  const campaignBaseData = useMemo(() => {
    if (selectedProjectId === 'all') return dashboardData;
    const anyKeyed = dashboardData.some(
      (r) => r.project_id != null && String(r.project_id) !== ''
    );
    if (!anyKeyed) return dashboardData;
    return dashboardData.filter(
      (r) => String(r.project_id) === String(selectedProjectId)
    );
  }, [dashboardData, selectedProjectId]);

  /** In-progress holes with coords for Planned drilling tab map (campaign scoped). */
  const plannedTabInProgressHoles = useMemo(
    () =>
      campaignBaseData.filter(
        (h) =>
          h.hole_status === 'In Progress' &&
          Number.isFinite(Number(h.latitude)) &&
          Number.isFinite(Number(h.longitude))
      ),
    [campaignBaseData]
  );

  const campaignTabList = useMemo(() => {
    if (projectSources.length > 0) return projectSources;
    const keys = [...new Set(dashboardData.map((r) => r.project_id).filter(Boolean))].sort();
    return keys.map((project_id) => ({ project_id }));
  }, [projectSources, dashboardData]);

  const sampleMetrics = useMemo(() => {
    if (!sampleProgressBundle) return null;
    if (selectedProjectId === 'all') {
      return sampleProgressBundle.aggregated ?? null;
    }
    const rows = sampleProgressBundle.projects || [];
    return (
      rows.find(
        (p) =>
          p.source_project_id != null &&
          String(p.source_project_id) === String(selectedProjectId)
      ) ?? null
    );
  }, [sampleProgressBundle, selectedProjectId]);

  const campaignTabLabel = useCallback(
    (src) => {
      const rows = sampleProgressBundle?.projects || [];
      const row = rows.find(
        (p) =>
          p.source_project_id != null &&
          String(p.source_project_id) === String(src.project_id)
      );
      return row?.source_project_name || String(src.project_id || 'Project');
    },
    [sampleProgressBundle]
  );

  /** Drilling program target depth (m): one project from registry, or sum of all projects' targets on All. */
  const defaultTargetDepthForSelection = useMemo(() => {
    if (!projectSources.length) return DEFAULT_PROGRAM_TARGET_M;
    if (selectedProjectId === 'all') {
      let sum = 0;
      let any = false;
      for (const p of projectSources) {
        const v = parseTargetDepthMeters(p.target);
        if (v != null) {
          sum += v;
          any = true;
        }
      }
      return any ? sum : DEFAULT_PROGRAM_TARGET_M;
    }
    const proj = projectSources.find(
      (p) => String(p.project_id) === String(selectedProjectId)
    );
    const v = proj ? parseTargetDepthMeters(proj.target) : null;
    return v != null ? v : DEFAULT_PROGRAM_TARGET_M;
  }, [projectSources, selectedProjectId]);

  useEffect(() => {
    setTargetDepth(defaultTargetDepthForSelection);
    setIsEditingTarget(false);
  }, [defaultTargetDepthForSelection]);

  // Extract unique zones, rigs, and years from the current campaign slice
  const uniqueZones = [...new Set(campaignBaseData.map(h => h.prospect).filter(Boolean))].sort();
  const uniqueRigs = [...new Set(campaignBaseData.map(h => h.drill_rig).filter(Boolean))].sort();
  const uniqueYears = [...new Set(campaignBaseData.map(h => getYearFromHoleId(h.hole_id)).filter(Boolean))].sort();
  
  // Build color maps
  const zoneColorMap = {};
  uniqueZones.forEach((zone, i) => {
    zoneColorMap[zone] = ZONE_COLORS[i % ZONE_COLORS.length];
  });
  
  const rigColorMap = {};
  uniqueRigs.forEach((rig, i) => {
    rigColorMap[rig] = RIG_COLORS[i % RIG_COLORS.length];
  });
  
  // Initialize zone/rig filters when the campaign slice changes
  useEffect(() => {
    if (campaignBaseData.length > 0) {
      const zones = [...new Set(campaignBaseData.map(h => h.prospect).filter(Boolean))];
      const newZoneFilters = {};
      zones.forEach(zone => { newZoneFilters[zone] = true; });
      setZoneFilters(newZoneFilters);

      const rigs = [...new Set(campaignBaseData.map(h => h.drill_rig).filter(Boolean))];
      const newRigFilters = {};
      rigs.forEach(rig => { newRigFilters[rig] = true; });
      setRigFilters(newRigFilters);
    }
  }, [campaignBaseData]);

  useEffect(() => {
    if (!selectedHoleId) return;
    const stillHere = campaignBaseData.some(h => h.hole_id === selectedHoleId);
    if (!stillHere) {
      setSelectedHoleId(null);
      setSelectedHole(null);
    }
  }, [campaignBaseData, selectedHoleId]);

  useEffect(() => {
    if (selectedProjectId === 'all') return;
    const valid = campaignTabList.some(
      (t) => t.project_id != null && String(t.project_id) === String(selectedProjectId)
    );
    if (campaignTabList.length > 0 && !valid) {
      setSelectedProjectId('all');
    }
  }, [campaignTabList, selectedProjectId]);

  useEffect(() => {
    if (!SHOW_PLANNED_DRILLING_TAB && activeTab === 'planned') {
      setActiveTab('visualization');
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'planned') setPlannedMapSelection(null);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    if (activeTab !== 'planned') return undefined;
    if (!plannedDateFrom || !plannedDateTo) return undefined;
    if (plannedDateFrom > plannedDateTo) {
      setPlannedDrillingError('Planned drilling: start date must be on or before end date.');
      setPlannedDrillingRows([]);
      setPlannedDrillingLoading(false);
      return undefined;
    }
    setPlannedDrillingLoading(true);
    setPlannedDrillingError(null);
    drillingService
      .getPlannedDrilling({
        date_from: plannedDateFrom,
        date_to: plannedDateTo,
        limit: 5000,
        sort: 'estimated_complete',
        order: 'asc',
      })
      .then((res) => {
        const rows = Array.isArray(res.data)
          ? res.data.filter((r) => plannedHoleHasLatLon(r) && estimatedCompleteIso(r))
          : [];
        if (!cancelled) setPlannedDrillingRows(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setPlannedDrillingRows([]);
          setPlannedDrillingError(err.message || String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setPlannedDrillingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, plannedDateFrom, plannedDateTo]);

  useEffect(() => {
    if (activeTab !== 'planned') return undefined;
    if (!plannedMapSelection) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPlannedMapSelection(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeTab, plannedMapSelection]);

  const resetPlannedTwoWeekWindow = useCallback(() => {
    const t = new Date();
    setPlannedDateFrom(isoCalendarDate(t));
    setPlannedDateTo(calendarDatePlusDays(t, 14));
    setPlannedMapSelection(null);
  }, []);

  const handleMapHoleClick = useCallback((hole) => {
    setSelectedHoleId(hole.hole_id);
    setSelectedHole(hole);
    setFocusTraceNonce((n) => n + 1);
  }, []);

  const closeHoleDetails = () => {
    setSelectedHoleId(null);
    setSelectedHole(null);
  };

  // Load drilling data, project registry, and per-project sample metrics
  useEffect(() => {
    const loadDrillingData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [drillingResult, sampleBundle, sourcesList, drillzRows] = await Promise.all([
          drillingService.fetchDashboardDrillingRows(),
          drillingService.fetchSampleProgressDashboard(),
          drillingService.fetchProjectSources().catch((e) => {
            console.warn('project_sources unavailable:', e);
            return [];
          }),
          drillingService.fetchDrillzCampaignProgress().catch(() => null),
        ]);

        setDashboardData(drillingResult.data || []);
        setSampleProgressBundle(sampleBundle);
        setProjectSources(Array.isArray(sourcesList) ? sourcesList : []);
        setDrillzCampaignRows(Array.isArray(drillzRows) ? drillzRows : null);
      } catch (err) {
        console.error('❌ Failed to load data:', err);
        // #region agent log
        fetch('http://127.0.0.1:7836/ingest/e71b2ef9-f9ac-414e-aecc-0212abf6b534', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a1699d' },
          body: JSON.stringify({
            sessionId: 'a1699d',
            runId: 'pre-fix',
            hypothesisId: 'A-E',
            location: 'DrillingDashboard.jsx:loadDrillingData',
            message: 'dashboard load failed',
            data: { errorMessage: err?.message || String(err) },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDrillingData();
  }, []);

  // Filter and sort data
  const filteredData = campaignBaseData
    .filter(row => {
      // Apply status filters (always active regardless of filter type)
      const activeStatusFilters = Object.keys(statusFilters).filter(k => statusFilters[k]);
      if (activeStatusFilters.length > 0 && !activeStatusFilters.includes(row.hole_status)) return false;
      
      // Apply zone filters when zone filter type is active
      if (filterType === 'zone' && Object.keys(zoneFilters).length > 0) {
        const activeZoneFilters = Object.keys(zoneFilters).filter(k => zoneFilters[k]);
        if (activeZoneFilters.length > 0 && !activeZoneFilters.includes(row.prospect)) return false;
      }
      
      // Apply rig filters when rig filter type is active
      if (filterType === 'rig' && Object.keys(rigFilters).length > 0) {
        const activeRigFilters = Object.keys(rigFilters).filter(k => rigFilters[k]);
        if (activeRigFilters.length > 0 && !activeRigFilters.includes(row.drill_rig)) return false;
      }
      
      // Apply year filter (always active regardless of filter type)
      if (yearFilter !== 'all') {
        const holeYear = getYearFromHoleId(row.hole_id);
        if (holeYear !== yearFilter) return false;
      }
      
      // Smart search - strip year prefix (e.g., '25SCM', '26SCM') for easier searching
      if (searchTerm) {
        const holeId = row.hole_id?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        // Remove common prefixes for matching
        const cleanHoleId = holeId.replace(/^\d{2}scm0*/i, '');
        // Match against full ID or clean ID
        if (!holeId.includes(search) && !cleanHoleId.includes(search)) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      return aVal > bVal ? direction : -direction;
    });

  // Calculate summary metrics from filtered data
  // Use filteredData to respect current status filters
  // Include Complete, In Progress, and Lost holes in totals (exclude Planned)
  const activeFilteredHoles = filteredData.filter(r => 
    r.hole_status === 'Complete' || r.hole_status === 'In Progress' || r.hole_status === 'Lost'
  );
  
  const metrics = {
    totalHoles: activeFilteredHoles.length,
    completedHoles: filteredData.filter(r => r.hole_status === 'Complete').length,
    inProgressHoles: filteredData.filter(r => r.hole_status === 'In Progress').length,
    totalDrilledDepth: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.final_depth) || parseFloat(r.current_depth) || 0), 0),
    totalLoggedDepth: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.logged_depth_m) || 0), 0),
    totalGeotechDepth: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.geotech_depth_m) || 0), 0),
    avgDrilledPct: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.drilled_pct) || 0), 0) / activeFilteredHoles.length || 0,
    avgLoggedPct: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.logged_pct) || 0), 0) / activeFilteredHoles.length || 0,
    avgSampledPct: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.sampled_pct) || 0), 0) / activeFilteredHoles.length || 0,
    avgAnalysisPct: activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.analysis_pct) || 0), 0) / activeFilteredHoles.length || 0,
    totalSamples: filteredData.reduce((sum, r) => sum + (parseInt(r.total_samples) || 0), 0),
    totalDrilledPct: (activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.final_depth) || parseFloat(r.current_depth) || 0), 0) / targetDepth) * 100,
    totalLoggedPct: (activeFilteredHoles.reduce((sum, r) => sum + (parseFloat(r.logged_depth_m) || 0), 0) / targetDepth) * 100
  };

  const drillzMetrics = useMemo(
    () =>
      resolveDrillzMetrics({
        drillzRows: drillzCampaignRows,
        projectSources,
        selectedProjectId,
      }),
    [drillzCampaignRows, projectSources, selectedProjectId]
  );

  const usingDrillzProgress = drillzMetrics.source === 'drillz';

  const displayDrilledDepth = usingDrillzProgress
    ? drillzMetrics.metersDrilled
    : metrics.totalDrilledDepth;
  const displayLoggedDepth = usingDrillzProgress
    ? drillzMetrics.metersLogged
    : metrics.totalLoggedDepth;
  const displayDrilledPct = targetDepth > 0 ? (displayDrilledDepth / targetDepth) * 100 : 0;
  const displayLoggedPct = targetDepth > 0 ? (displayLoggedDepth / targetDepth) * 100 : 0;
  const displayCutDepth = usingDrillzProgress ? drillzMetrics.metersCut : 0;
  const displayCutPct = targetDepth > 0 ? (displayCutDepth / targetDepth) * 100 : 0;

  const displaySampleMetrics = useMemo(() => {
    if (!sampleMetrics) return null;
    const designated = usingDrillzProgress
      ? drillzMetrics.samplesDesignated
      : parseInt(sampleMetrics.total_samples_designated || 0, 10);
    const bagged = usingDrillzProgress
      ? drillzMetrics.samplesInBag
      : parseInt(sampleMetrics.total_samples_bagged || 0, 10);
    return {
      ...sampleMetrics,
      total_samples_designated: designated,
      total_samples_bagged: bagged,
    };
  }, [sampleMetrics, usingDrillzProgress, drillzMetrics.samplesDesignated, drillzMetrics.samplesInBag]);

  const drilledSourceTable = usingDrillzProgress
    ? DRILLZ_CAMPAIGN_PROGRESS_VIEW
    : DASHBOARD_DATA_SOURCES.holeSumDrilled;
  const loggedSourceTable = usingDrillzProgress
    ? DRILLZ_CAMPAIGN_PROGRESS_VIEW
    : DASHBOARD_DATA_SOURCES.holeSumLogged;
  const designatedSourceTable = usingDrillzProgress
    ? DRILLZ_CAMPAIGN_PROGRESS_VIEW
    : DASHBOARD_DATA_SOURCES.sampleProgress;
  const baggedSourceTable = usingDrillzProgress
    ? DRILLZ_CAMPAIGN_PROGRESS_VIEW
    : DASHBOARD_DATA_SOURCES.sampleProgress;

  // Progress bar component (compact: sidebar hole details; default: main table columns)
  const ProgressBar = ({ value, label, showLabel = true, compact = false }) => {
    const pct = parseFloat(value) || 0;
    const color = pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : pct >= 50 ? '#3b82f6' : '#ef4444';

    if (compact) {
      return (
        <div style={{ width: '100%', minWidth: 0 }}>
          {showLabel && (
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '3px', lineHeight: 1.3 }}>
              <span>{label}</span>
              <span style={{ fontWeight: '600', color: '#e5e7eb', marginLeft: '0.35rem' }}>{pct.toFixed(1)}%</span>
            </div>
          )}
          <div style={{ width: '100%', height: '6px', backgroundColor: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', backgroundColor: color, transition: 'width 0.3s ease', borderRadius: '3px' }} />
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', minWidth: '120px' }}>
        {showLabel && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem', color: '#9ca3af' }}>
            <span>{label}</span>
            <span style={{ fontWeight: '600', color: '#e5e7eb' }}>{pct.toFixed(1)}%</span>
          </div>
        )}
        <div style={{ width: '100%', height: '8px', backgroundColor: '#1f2937', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', backgroundColor: color, transition: 'width 0.3s ease', borderRadius: '4px' }} />
        </div>
      </div>
    );
  };

  // Export to CSV function
  const exportToCSV = () => {
    const headers = ['Hole ID', 'Status', 'Drilled %', 'Logged %', 'Geotech %', 'Sampled %', 'Analysis %', 'Total Samples', 'Samples Packed'];
    const rows = filteredData.map(row => [
      row.hole_id,
      row.hole_status,
      row.drilled_pct || '',
      row.logged_pct || '',
      row.geoteched_pct || '',
      row.sampled_pct || '',
      row.analysis_pct || '',
      row.total_samples || '',
      row.samples_packed || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drilling-progress-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Table columns for drilling progress metrics
  const drillingColumns = [
    {
      key: 'hole_id',
      title: 'Hole ID',
      render: (value, row) => (
        <div className="hole-cell" style={{ padding: '0.5rem 0' }}>
          <strong style={{ color: '#f9be0b', fontSize: '0.95rem', display: 'block' }}>{value}</strong>
          <span style={{ 
            fontSize: '0.65rem',
            color: row.hole_status === 'Complete' ? '#10b981' : row.hole_status === 'In Progress' ? '#3b82f6' : '#6b7280',
            fontWeight: '500',
            display: 'block',
            marginTop: '2px'
          }}>
            {row.hole_status}
          </span>
        </div>
      )
    },
    {
      key: 'drilled_pct',
      title: 'Drilled Progress',
      render: (value) => <ProgressBar value={value} label="Drilled" />
    },
    {
      key: 'logged_pct',
      title: 'Logged Progress',
      render: (value) => <ProgressBar value={value} label="Logged" />
    },
    {
      key: 'geoteched_pct',
      title: 'Geotech Progress',
      render: (value) => <ProgressBar value={value} label="Geotech" />
    },
    {
      key: 'sampled_pct',
      title: 'Sampled Progress',
      render: (value) => <ProgressBar value={value} label="Sampled" />
    },
    {
      key: 'analysis_pct',
      title: 'Analysis Progress',
      render: (value) => <ProgressBar value={value} label="Analysis" />
    },
    {
      key: 'total_samples',
      title: 'Samples',
      render: (value, row) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#e5e7eb' }}>
            {value || 0}
          </div>
          {row.samples_packed > 0 && (
            <div style={{ fontSize: '0.7rem', color: '#10b981' }}>
              {row.samples_packed} packed
            </div>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <Layout user={user} currentRoute="/drilling-dashboard">
        <div className="drilling-loading">
          <Spinner size="large" />
          <h2>Loading Drilling Dashboard...</h2>
          <p>Fetching data from Postgres database...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
  return (
    <Layout user={user} currentRoute="/drilling-dashboard">
      <div className="drilling-dashboard">
        <div className="dashboard-header">
          <div className="header-content">
              <IconText icon={ICON.appDrillingDashboard} as="h1" className="dashboard-title">Drilling Dashboard</IconText>
              <p className="dashboard-subtitle">Real-time drilling operations monitoring and analytics</p>
            </div>
          </div>
          
          <Card variant="elevated" className="error-card">
            <div className="error-content">
              <IconText icon={ICON.uiError} as="h3">Failed to Load Drilling Data</IconText>
              <p className="error-message">{error}</p>
              <p className="error-hint">
                Make sure you can connect to your database:
                <br />• Table: drilling_progress.drilling_master_2025
                <br />• Check POSTGRES_CONNECTION_STRING environment variable
                <br />• Verify Azure Function is deployed and running
              </p>
              <Button 
                variant="primary" 
                onClick={() => window.location.reload()}
                style={{ marginTop: '1rem' }}
              >
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} currentRoute="/drilling-dashboard" fullWidth={true}>
      <div className="drilling-dashboard">
        {campaignTabList.length > 0 && (
          <div className="campaign-tab-bar" role="tablist" aria-label="Drilling campaign">
            <button
              type="button"
              role="tab"
              aria-selected={selectedProjectId === 'all'}
              className={`campaign-tab-btn${selectedProjectId === 'all' ? ' campaign-tab-btn--active' : ''}`}
              onClick={() => setSelectedProjectId('all')}
            >
              All
            </button>
            {campaignTabList.map((src) => (
              <button
                key={String(src.project_id)}
                type="button"
                role="tab"
                aria-selected={selectedProjectId === String(src.project_id)}
                className={`campaign-tab-btn${selectedProjectId === String(src.project_id) ? ' campaign-tab-btn--active' : ''}`}
                onClick={() => setSelectedProjectId(String(src.project_id))}
              >
                {campaignTabLabel(src)}
              </button>
            ))}
          </div>
        )}
        {/* Sticky Compact Metrics Header */}
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 1000,
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          borderBottom: '2px solid #f9be0b',
          padding: '1rem 2rem',
          marginBottom: '0',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Compact Metrics Row - First row: Drilling metrics */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'nowrap', overflow: 'visible' }}>
            <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.drillingProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f9be0b', whiteSpace: 'nowrap' }}>{metrics.totalHoles}</div>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Total Holes</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{metrics.completedHoles} Complete</div>
            </div>
            
            <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.drillingProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444', whiteSpace: 'nowrap' }}>{metrics.inProgressHoles}</div>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>In Progress</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Active</div>
            </div>
            
            <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.drillingProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981', whiteSpace: 'nowrap' }}>{metrics.avgDrilledPct.toFixed(1)}%</div>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Avg Drilled</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Completion</div>
            </div>
            
            <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.drillingProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6', whiteSpace: 'nowrap' }}>{metrics.avgLoggedPct.toFixed(1)}%</div>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Avg Logged</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Documentation</div>
            </div>
            
            {displaySampleMetrics && (
              <div title={sourceTooltip(designatedSourceTable)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#a855f7', whiteSpace: 'nowrap' }}>{parseInt(displaySampleMetrics.total_samples_designated || 0).toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Designated</div>
                <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Samples</div>
              </div>
            )}
            
            {displaySampleMetrics && (
              <div title={sourceTooltip(baggedSourceTable)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f59e0b', whiteSpace: 'nowrap' }}>{parseInt(displaySampleMetrics.total_samples_bagged || 0).toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Bagged</div>
                <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_bagged / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%</div>
              </div>
            )}
            
            {displaySampleMetrics && (
              <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#06b6d4', whiteSpace: 'nowrap' }}>{parseInt(displaySampleMetrics.total_samples_received_by_lab || 0).toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>At Lab</div>
                <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_received_by_lab / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%</div>
              </div>
            )}
            
            {displaySampleMetrics && (
              <div title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)} style={{ flex: '1', minWidth: '100px', textAlign: 'center', padding: '0.5rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '6px', border: '1px solid #374151', cursor: 'help' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6', whiteSpace: 'nowrap' }}>{parseInt(displaySampleMetrics.total_samples_finalized_by_lab || 0).toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap' }}>Finalized</div>
                <div style={{ fontSize: '0.65rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_finalized_by_lab / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%</div>
              </div>
            )}
            </div>
          
          {/* Compact Program Progress - Second row */}
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }} title={sourceTooltip(DASHBOARD_DATA_SOURCES.projectTarget)}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f9fafb', whiteSpace: 'nowrap' }}>
                <IconText icon={ICON.drillFilterStatus} iconSize={14} as="span">Target:</IconText>
              </span>
              {isEditingTarget ? (
                <input
                  type="number"
                  value={targetDepth}
                  onChange={(e) => setTargetDepth(parseInt(e.target.value, 10) || DEFAULT_PROGRAM_TARGET_M)}
                  onBlur={() => setIsEditingTarget(false)}
                  onKeyPress={(e) => e.key === 'Enter' && setIsEditingTarget(false)}
                  autoFocus
                  style={{
                    width: '90px',
                    padding: '0.25rem 0.5rem',
                    background: '#1f2937',
                    border: '1px solid #f9be0b',
                    borderRadius: '4px',
                    color: '#f9be0b',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                />
              ) : (
                <span 
                  onClick={() => setIsEditingTarget(true)}
                  style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '700', 
                    color: '#f9be0b',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    border: '1px dashed #374151',
                    whiteSpace: 'nowrap'
                  }}
                  title={sourceTooltip(DASHBOARD_DATA_SOURCES.projectTarget)}
                >
                  {targetDepth.toLocaleString()}m
                </span>
              )}
            </div>
            
            <div style={{ flex: '1', minWidth: '200px' }} title={sourceTooltip(drilledSourceTable)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem' }}>
                <span style={{ color: '#9ca3af' }}>Total Drilled</span>
                <span style={{ color: '#f9be0b', fontWeight: '600', whiteSpace: 'nowrap' }}>{displayDrilledDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m ({displayDrilledPct.toFixed(1)}%)</span>
              </div>
              <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(displayDrilledPct, 100)}%`, height: '100%', background: 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
              </div>
            </div>
            
            <div style={{ flex: '1', minWidth: '200px' }} title={sourceTooltip(loggedSourceTable)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem' }}>
                <span style={{ color: '#9ca3af' }}>Total Logged</span>
                <span style={{ color: '#10b981', fontWeight: '600', whiteSpace: 'nowrap' }}>{displayLoggedDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m ({displayLoggedPct.toFixed(1)}%)</span>
              </div>
              <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(displayLoggedPct, 100)}%`, height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
              </div>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }} title={sourceTooltip(DRILLZ_CAMPAIGN_PROGRESS_VIEW)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem' }}>
                <span style={{ color: '#9ca3af' }}>Total Cut</span>
                <span style={{ color: '#818cf8', fontWeight: '600', whiteSpace: 'nowrap' }}>{displayCutDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m ({displayCutPct.toFixed(1)}%)</span>
              </div>
              <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(displayCutPct, 100)}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
              </div>
            </div>
          </div>

          {/* Sample Processing Progress - Third row */}
          {displaySampleMetrics && (
            <div style={{
              marginTop: '0.75rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid #374151',
              display: 'flex',
              gap: '1rem',
              flexWrap: 'nowrap'
            }}>
              <div style={{ flex: '1', minWidth: '180px' }} title={sourceTooltip(baggedSourceTable)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.7rem' }}>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>Bagged</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {parseInt(displaySampleMetrics.total_samples_bagged || 0).toLocaleString()} / {parseInt(displaySampleMetrics.total_samples_designated || 0).toLocaleString()}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${displaySampleMetrics.total_samples_designated ? Math.min((displaySampleMetrics.total_samples_bagged / displaySampleMetrics.total_samples_designated) * 100, 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                  {displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_bagged / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '180px' }} title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.7rem' }}>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>At Lab</span>
                  <span style={{ color: '#06b6d4', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {parseInt(displaySampleMetrics.total_samples_received_by_lab || 0).toLocaleString()}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${displaySampleMetrics.total_samples_designated ? Math.min((displaySampleMetrics.total_samples_received_by_lab / displaySampleMetrics.total_samples_designated) * 100, 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg, #06b6d4 0%, #0891b2 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                  {displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_received_by_lab / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '180px' }} title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.7rem' }}>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>Finalized</span>
                  <span style={{ color: '#8b5cf6', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {parseInt(displaySampleMetrics.total_samples_finalized_by_lab || 0).toLocaleString()}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${displaySampleMetrics.total_samples_designated ? Math.min((displaySampleMetrics.total_samples_finalized_by_lab / displaySampleMetrics.total_samples_designated) * 100, 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                  {displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_finalized_by_lab / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '180px' }} title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.7rem' }}>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>QAQC Passed</span>
                  <span style={{ color: '#10b981', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {parseInt(displaySampleMetrics.total_samples_qaqc_passed || 0).toLocaleString()}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${displaySampleMetrics.total_samples_designated ? Math.min((displaySampleMetrics.total_samples_qaqc_passed / displaySampleMetrics.total_samples_designated) * 100, 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                  {displaySampleMetrics.total_samples_designated ? ((displaySampleMetrics.total_samples_qaqc_passed / displaySampleMetrics.total_samples_designated) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>
            </div>
          )}

          {/* Sequential Leach Progress - Fourth row */}
          {sampleMetrics && sampleMetrics.sequential_leach_samples_received_by_lab > 0 && (
            <div style={{ 
              marginTop: '0.75rem', 
              paddingTop: '0.75rem', 
              borderTop: '1px solid #374151',
              display: 'flex', 
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'nowrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#f9fafb', whiteSpace: 'nowrap' }}>
                  <IconText icon={ICON.uiSequentialLeach} iconSize={14} as="span">Sequential Leach:</IconText>
                </span>
              </div>
              <div style={{ flex: '1', minWidth: '200px' }} title={sourceTooltip(DASHBOARD_DATA_SOURCES.sampleProgress)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.7rem' }}>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>Finalized</span>
                  <span style={{ color: '#f43f5e', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {parseInt(sampleMetrics.sequential_leach_samples_finalized_by_lab || 0).toLocaleString()} / {parseInt(sampleMetrics.sequential_leach_samples_received_by_lab || 0).toLocaleString()}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1f2937', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((sampleMetrics.sequential_leach_samples_finalized_by_lab / sampleMetrics.sequential_leach_samples_received_by_lab) * 100, 100)}%`, height: '100%', background: 'linear-gradient(90deg, #f43f5e 0%, #e11d48 100%)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                  {((sampleMetrics.sequential_leach_samples_finalized_by_lab / sampleMetrics.sequential_leach_samples_received_by_lab) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div style={{ padding: '1rem 2rem', width: '100%', boxSizing: 'border-box' }}>
        
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1rem',
          borderBottom: '2px solid #374151'
        }}>
          <button
            onClick={() => setActiveTab('visualization')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'visualization' ? '#f9be0b' : 'transparent',
              color: activeTab === 'visualization' ? '#111827' : '#9ca3af',
              border: 'none',
              borderBottom: activeTab === 'visualization' ? '3px solid #f9be0b' : '3px solid transparent',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
              Drill Hole Visualization
          </button>
          {SHOW_PLANNED_DRILLING_TAB ? (
            <button
              type="button"
              onClick={() => setActiveTab('planned')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'planned' ? '#f9be0b' : 'transparent',
                color: activeTab === 'planned' ? '#111827' : '#9ca3af',
                border: 'none',
                borderBottom: activeTab === 'planned' ? '3px solid #f9be0b' : '3px solid transparent',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '-2px',
              }}
            >
              Planned drilling map
            </button>
          ) : null}
          <button
            onClick={() => setActiveTab('coring')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'coring' ? '#f9be0b' : 'transparent',
              color: activeTab === 'coring' ? '#111827' : '#9ca3af',
              border: 'none',
              borderBottom: activeTab === 'coring' ? '3px solid #f9be0b' : '3px solid transparent',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
              Coring Activity
          </button>
          <button
            onClick={() => setActiveTab('coring-data')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'coring-data' ? '#f9be0b' : 'transparent',
              color: activeTab === 'coring-data' ? '#111827' : '#9ca3af',
              border: 'none',
              borderBottom: activeTab === 'coring-data' ? '3px solid #f9be0b' : '3px solid transparent',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
              Coring data
          </button>
        </div>

        {/* Tab Content - Visualization */}
        {activeTab === 'visualization' && (
          <Card variant="elevated" padding="none" className="viz-card" style={{ marginBottom: '1rem' }}>
            <div className="viz-toolbar">
              <div className="viz-toolbar-left">
                <h3 className="viz-toolbar-title">
                  Drill Hole Map & 3D View
                </h3>

                {/* Filter Type Tabs */}
                <div className="viz-filter-group">
                  {[
                    { type: 'status', label: 'Status', iconId: ICON.drillFilterStatus },
                    { type: 'zone', label: 'Zone', iconId: ICON.drillFilterZone },
                    { type: 'rig', label: 'Drill Rig', iconId: ICON.drillFilterRig },
                    { type: 'progress', label: 'Progress', iconId: ICON.drillFilterProgress },
                  ].map(({ type, label, icon }) => (
                    <button
                      key={type}
                      onClick={() => setFilterType(type)}
                      className={`viz-filter-btn${filterType === type ? ' active' : ''}`}
                    >
                      {icon} {label}
                    </button>
                  ))}
                </div>

                {/* Year Filter */}
                <div className="viz-filter-group">
                  <span className="viz-year-label"><IconText icon={ICON.uiCalendarDate} iconSize={14} as="span">Year:</IconText></span>
                  <select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className={`viz-year-select${yearFilter !== 'all' ? ' active' : ''}`}
                  >
                    <option value="all">All Years</option>
                    {uniqueYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                {/* 3D View Toggle */}
                <button
                  onClick={() => setShow3DView(!show3DView)}
                  className={`viz-toggle-btn${show3DView ? ' active' : ''}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                  3D View {show3DView ? 'ON' : 'OFF'}
                </button>
              </div>

              <Button variant="ghost" size="sm" onClick={async () => {
                const mapComponent = document.querySelector('.drill-hole-map');
                if (!mapComponent) return;
                try {
                  await downloadDrillHoleMapPng(
                    mapComponent,
                    `drill-hole-map-${new Date().toISOString().split('T')[0]}.png`
                  );
                } catch (err) {
                  console.error('Failed to download map:', err);
                }
              }}>
                <IconText icon={ICON.uiCameraMap} iconSize={14}>Download Map</IconText>
              </Button>
            </div>

          <PanelGroup direction="horizontal" style={{ flex: 1, minHeight: 0 }}>
            {/* Map Panel — with 3D+details: 45%; map+details only: 70%; no selection: full width */}
            <Panel
              defaultSize={selectedHole ? (show3DView ? 45 : 70) : 100}
              minSize={30}
            >
              <div style={{ height: '100%', padding: '0.5rem' }}>
                <DrillHoleMap 
                  data={filteredData} 
                  onHoleClick={handleMapHoleClick}
                  selectedHoleId={selectedHoleId}
                  filterType={filterType}
                  statusFilters={statusFilters}
                  zoneFilters={zoneFilters}
                  rigFilters={rigFilters}
                  zoneColorMap={zoneColorMap}
                  rigColorMap={rigColorMap}
                  progressRingVisibility={progressRingVisibility}
                  onProgressMetricToggle={toggleProgressRingMetric}
                  onStatusToggle={toggleStatusFilter}
                  onZoneToggle={toggleZoneFilter}
                  onRigToggle={toggleRigFilter}
                />
              </div>
            </Panel>
            
            {/* 3D Viewer Panel - Only show when toggled ON and hole selected */}
            {show3DView && selectedHole && (
              <>
                <PanelResizeHandle style={{ width: '4px', background: '#374151', cursor: 'col-resize' }} />
                <Panel defaultSize={33} minSize={18}>
                  <div style={{ height: '100%', padding: '0.5rem', background: '#111827', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#f9be0b', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                        <path d="M2 17l10 5 10-5"/>
                        <path d="M2 12l10 5 10-5"/>
                      </svg>
                      3D View
                    </h4>
                    {viewer3DPoppedOut && (
                      <div
                        className="viewer-popout-placeholder"
                        onClick={() => setViewer3DPoppedOut(false)}
                        style={{ flexShrink: 0 }}
                      >
                        <span>3D Viewer is in a separate window</span>
                        <button type="button" className="viewer-popout-dock-btn">Dock</button>
                      </div>
                    )}
                    {/*
                      Mount node stays in layout while popped out (hidden) so the portaled viewer
                      keeps non-zero size until the popup container is ready, then portal target switches.
                    */}
                    <div
                      ref={setDock3dMountEl}
                      style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        visibility: viewer3DPoppedOut ? 'hidden' : 'visible',
                        pointerEvents: viewer3DPoppedOut ? 'none' : 'auto',
                      }}
                    />
                  </div>
                </Panel>
              </>
            )}
            {viewer3DPoppedOut && selectedHole && (
              <PopoutWindow
                title={`3D Drill Trace - ${selectedHoleId}`}
                width={1200}
                height={800}
                onClose={() => setViewer3DPoppedOut(false)}
                onMountTargetReady={setPopout3dMountEl}
              />
            )}
            {show3DView &&
              selectedHole &&
              selectedHoleId &&
              (viewer3DPoppedOut ? popout3dMountEl ?? dock3dMountEl : dock3dMountEl) &&
              createPortal(
                <DrillHole3DViewer
                  selectedHoleId={selectedHoleId}
                  focusTraceNonce={focusTraceNonce}
                  isPoppedOut={viewer3DPoppedOut}
                  onPopoutToggle={() => setViewer3DPoppedOut((v) => !v)}
                />,
                viewer3DPoppedOut ? popout3dMountEl ?? dock3dMountEl : dock3dMountEl,
              )}
            
            {/* Hole Details Panel - Only show when hole selected */}
            {selectedHole && (
              <>
                <PanelResizeHandle style={{ width: '4px', background: '#374151', cursor: 'col-resize' }} />
                <Panel
                  defaultSize={show3DView ? 22 : 30}
                  minSize={15}
                >
                  <div className="hole-details-scroll" style={{ height: '100%', padding: '0.35rem', overflowY: 'auto' }}>
              <div className="hole-details-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0, color: '#f9be0b', fontSize: '1.25rem' }}>
                    {selectedHole.hole_id}
                  </h3>
                  <button 
                    onClick={closeHoleDetails}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#9ca3af',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      padding: '0',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span style={{ 
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: selectedHole.hole_status === 'Complete' ? '#10b981' : 
                                    selectedHole.hole_status === 'In Progress' ? '#ef4444' : '#6b7280',
                    color: 'white',
                    width: 'fit-content'
                  }}>
                    {selectedHole.hole_status}
                  </span>
                  
                  {/* Zone (Prospect) and Drill Rig Info */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                    {selectedHole.prospect && (
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: zoneColorMap[selectedHole.prospect] || '#6b7280',
                        color: 'white'
                      }}>
                        <><SelkirkIcon name={ICON.drillFilterZone} size={14} decorative /> {selectedHole.prospect}</>
                      </span>
                    )}
                    {selectedHole.drill_rig && (
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: rigColorMap[selectedHole.drill_rig] || '#6b7280',
                        color: 'white'
                      }}>
                        <><SelkirkIcon name={ICON.drillFilterRig} size={14} decorative /> {selectedHole.drill_rig}</>
                      </span>
                    )}
                    {selectedHole.pad_id && (
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: '#374151',
                        color: '#e5e7eb'
                      }}>
                        <><SelkirkIcon name={ICON.uiLocationPin} size={14} decorative /> Pad {selectedHole.pad_id}</>
                      </span>
                    )}
                  </div>
                </div>

                {([
                  selectedHole.master_target_lens,
                  selectedHole.master_target_lens_m,
                  selectedHole.master_azimuth,
                  selectedHole.master_dip,
                  selectedHole.master_phase,
                  selectedHole.master_comments,
                ].some(isHoleSetupValuePresent)) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 className="hole-details-section-title">Hole setup (master)</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {isHoleSetupValuePresent(selectedHole.master_target_lens) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Target lens:</span>
                          <span className="hole-details-value hole-details-value--wrap">{selectedHole.master_target_lens}</span>
                        </div>
                      )}
                      {isHoleSetupValuePresent(selectedHole.master_target_lens_m) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Target lens (m):</span>
                          <span className="hole-details-value">{formatHoleMasterLensM(selectedHole.master_target_lens_m)}</span>
                        </div>
                      )}
                      {isHoleSetupValuePresent(selectedHole.master_azimuth) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Azimuth:</span>
                          <span className="hole-details-value">{formatHoleMasterLensM(selectedHole.master_azimuth)}</span>
                        </div>
                      )}
                      {isHoleSetupValuePresent(selectedHole.master_dip) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Dip:</span>
                          <span className="hole-details-value">{formatHoleMasterLensM(selectedHole.master_dip)}</span>
                        </div>
                      )}
                      {isHoleSetupValuePresent(selectedHole.master_phase) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Phase:</span>
                          <span className="hole-details-value hole-details-value--wrap">{selectedHole.master_phase}</span>
                        </div>
                      )}
                      {isHoleSetupValuePresent(selectedHole.master_comments) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Comments:</span>
                          <span className="hole-details-value hole-details-value--wrap">{selectedHole.master_comments}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <h4 className="hole-details-section-title">Collar / trace (EPSG:26908)</h4>
                  <p style={{ margin: '0 0 0.45rem', fontSize: '0.65rem', color: '#9ca3af', lineHeight: 1.35 }}>
                    Collar coordinates from drilling_scmi.mv_traces geometry (trace start).
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <div className="hole-details-row">
                      <span className="hole-details-label">Easting:</span>
                      <span className="hole-details-value">{formatHoleTraceCoord26908(selectedHole.trace_collar_easting_26908)}</span>
                    </div>
                    <div className="hole-details-row">
                      <span className="hole-details-label">Northing:</span>
                      <span className="hole-details-value">{formatHoleTraceCoord26908(selectedHole.trace_collar_northing_26908)}</span>
                    </div>
                    <div className="hole-details-row">
                      <span className="hole-details-label">Z:</span>
                      <span className="hole-details-value">{formatHoleTraceCoord26908(selectedHole.trace_collar_z_26908)}</span>
                    </div>
                  </div>
                </div>

                {/* Progress Section */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 className="hole-details-section-title">
                    <IconText icon={ICON.uiChartAnalytics} iconSize={14}>Progress Metrics</IconText>
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                    <ProgressBar value={selectedHole.drilled_pct} label="Drilled" compact />
                    <ProgressBar value={selectedHole.logged_pct} label="Logged" compact />
                    <ProgressBar value={selectedHole.geoteched_pct} label="Geotech" compact />
                    <ProgressBar value={selectedHole.sampled_pct} label="Sampled" compact />
                    {selectedHole.analysis_pct > 0 && (
                      <ProgressBar value={selectedHole.analysis_pct} label="Analyzed" compact />
                    )}
                </div>
              </div>

                {/* Depths Section */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 className="hole-details-section-title">
                    <IconText icon={ICON.uiDepth} iconSize={14}>Depths</IconText>
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {(selectedHole.final_depth || selectedHole.deepest_survey) && (
                      <div className="hole-details-row">
                        <span className="hole-details-label">Hole Depth:</span>
                        <span className="hole-details-value hole-details-value--accent">
                          {parseFloat(selectedHole.final_depth || selectedHole.deepest_survey).toFixed(1)}m
                        </span>
                      </div>
                    )}
                    {selectedHole.planned_depth && (
                      <div className="hole-details-row">
                        <span className="hole-details-label">Planned Depth:</span>
                        <span className="hole-details-value">
                          {parseFloat(selectedHole.planned_depth).toFixed(1)}m
                        </span>
                      </div>
                    )}
                    {selectedHole.target_lens &&
                      !isHoleSetupValuePresent(selectedHole.master_target_lens) && (
                      <div className="hole-details-row">
                        <span className="hole-details-label">Target Lens:</span>
                        <span className="hole-details-value hole-details-value--wrap">
                          {selectedHole.target_lens}
                        </span>
                      </div>
                    )}
                    {selectedHole.logged_depth_m && (
                      <div className="hole-details-row">
                        <span className="hole-details-label">Logged:</span>
                        <span className="hole-details-value">
                          {parseFloat(selectedHole.logged_depth_m).toFixed(1)}m
                        </span>
                      </div>
                    )}
                    {selectedHole.geotech_depth_m && (
                      <div className="hole-details-row">
                        <span className="hole-details-label">Geotech:</span>
                        <span className="hole-details-value">
                          {parseFloat(selectedHole.geotech_depth_m).toFixed(1)}m
                        </span>
                      </div>
                    )}
          </div>
        </div>

                {/* Timeline Section */}
                {selectedHole.start_date && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 className="hole-details-section-title">
                      <IconText icon={ICON.uiCalendarDate} iconSize={14}>Timeline</IconText>
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {selectedHole.start_date && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Start Date:</span>
                          <span className="hole-details-value">
                            {new Date(selectedHole.start_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {(selectedHole.total_days || selectedHole.start_date) && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Total Days:</span>
                          <span className="hole-details-value">
                            {selectedHole.total_days ||
                              Math.floor((new Date() - new Date(selectedHole.start_date)) / (1000 * 60 * 60 * 24))} days
                          </span>
                        </div>
                      )}
                    </div>
            </div>
                )}

                {/* Samples Section */}
                {selectedHole.total_samples > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 className="hole-details-section-title">
                      <IconText icon={ICON.uiSequentialLeach} iconSize={14}>Samples</IconText>
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <div className="hole-details-row">
                        <span className="hole-details-label">Total:</span>
                        <span className="hole-details-value">
                          {selectedHole.total_samples}
                    </span>
                  </div>
                      {selectedHole.samples_packed > 0 && (
                        <div className="hole-details-row">
                          <span className="hole-details-label">Packed:</span>
                          <span className="hole-details-value hole-details-value--success">
                            {selectedHole.samples_packed}
                          </span>
                    </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sample Dispatch Info */}
                {selectedHole.sample_dispatch_numbers && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 className="hole-details-section-title">
                      <IconText icon={ICON.uiDispatch} iconSize={14}>Dispatch Info</IconText>
                    </h4>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {selectedHole.sample_dispatch_numbers}
                    </div>
                    {selectedHole.sample_dispatch_statuses && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                        Status: {selectedHole.sample_dispatch_statuses}
                      </div>
                    )}
                  </div>
                )}

                {/* Workorder Info */}
                {selectedHole.workorder_numbers && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 className="hole-details-section-title">
                      <IconText icon={ICON.uiClipboardList} iconSize={14}>Workorders</IconText>
                    </h4>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {selectedHole.workorder_numbers}
                    </div>
                    {selectedHole.workorder_statuses && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                        Status: {selectedHole.workorder_statuses}
                </div>
                    )}
            </div>
                )}

                {/* ETA */}
                {selectedHole.final_eta && (
                  <div style={{ 
                    padding: '0.75rem',
                    background: 'rgba(249, 190, 11, 0.1)',
                    border: '1px solid rgba(249, 190, 11, 0.3)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}>
                    <div style={{ color: '#f9be0b', fontWeight: '600', marginBottom: '0.25rem' }}>
                      <IconText icon={ICON.uiClockEta} iconSize={14}>ETA</IconText>
            </div>
                    <div style={{ color: '#e5e7eb' }}>
                      {selectedHole.final_eta}
                    </div>
                  </div>
                )}
                </div>
            </div>
                </Panel>
              </>
            )}
          </PanelGroup>
          </Card>
        )}

        {SHOW_PLANNED_DRILLING_TAB && activeTab === 'planned' && (
        <>
          <Card variant="elevated" padding="none" className="viz-card" style={{ marginBottom: '1rem' }}>
            <div
              style={{
                padding: '0.85rem 1rem',
                borderBottom: '1px solid #374151',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#f9be0b', fontWeight: 600 }}>
                <IconText icon={ICON.uiCalendarDate} iconSize={14}>Planned drilling & in-progress pads</IconText>
              </h3>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.72rem', color: '#9ca3af' }}>
                From
                <input
                  type="date"
                  value={plannedDateFrom}
                  onChange={(e) => {
                    setPlannedMapSelection(null);
                    setPlannedDateFrom(e.target.value);
                  }}
                  style={{
                    marginTop: 2,
                    padding: '0.35rem',
                    background: '#1f2937',
                    border: '1px solid #374151',
                    color: '#e5e7eb',
                    borderRadius: 4,
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.72rem', color: '#9ca3af' }}>
                Through
                <input
                  type="date"
                  value={plannedDateTo}
                  onChange={(e) => {
                    setPlannedMapSelection(null);
                    setPlannedDateTo(e.target.value);
                  }}
                  style={{
                    marginTop: 2,
                    padding: '0.35rem',
                    background: '#1f2937',
                    border: '1px solid #374151',
                    color: '#e5e7eb',
                    borderRadius: 4,
                  }}
                />
              </label>
              <Button variant="ghost" size="sm" type="button" onClick={resetPlannedTwoWeekWindow}>
                Today + 14 days
              </Button>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {plannedDrillingRows.length} dated planned row(s) · {plannedTabInProgressHoles.length} in-progress hole(s) on map
              </span>
              {plannedDrillingError ? (
                <span style={{ color: '#fca5a5', fontSize: '0.8rem' }}>{plannedDrillingError}</span>
              ) : null}
            </div>

            <div style={{ height: 560, minHeight: 520 }}>
              <PlannedDrillingMap
                plannedRows={plannedDrillingRows}
                inProgressHoles={plannedTabInProgressHoles}
                rigColorMap={rigColorMap}
                loading={plannedDrillingLoading}
                windowStartISO={plannedDateFrom}
                windowEndISO={plannedDateTo}
                selectedFeature={plannedMapSelection}
                onSelectFeature={setPlannedMapSelection}
              />
            </div>
          </Card>

          {SHOW_PLANNED_DRILLING_TAB && activeTab === 'planned' && plannedMapSelection && typeof document !== 'undefined'
            ? createPortal(
                <div
                  className="planned-drilling-detail-modal-overlay"
                  onClick={() => setPlannedMapSelection(null)}
                  role="presentation"
                >
                  <div
                    className="planned-drilling-detail-modal-shell"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="planned-drilling-modal-title"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="planned-drilling-detail-modal-head">
                      <h4 id="planned-drilling-modal-title">
                        {plannedMapSelection.type === 'in_progress'
                          ? plannedMapSelection.row?.hole_id
                          : `${plannedMapSelection.row?.hole_id || plannedMapSelection.row?.prop_hole_id || 'Planned hole'}`}
                      </h4>
                      <button
                        type="button"
                        className="planned-drilling-detail-modal-close"
                        onClick={() => setPlannedMapSelection(null)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="planned-drilling-detail-modal-body hole-details-scroll">
                      {plannedMapSelection.type === 'in_progress' && plannedMapSelection.row ? (
                        <div style={{ color: '#e5e7eb' }}>
                          <PlannedDetailKv label="Status" value="In progress (master)" />
                          <PlannedDetailKv label="Pad" value={plannedMapSelection.row.pad_id} />
                          <PlannedDetailKv label="Prospect" value={plannedMapSelection.row.prospect} />
                          <PlannedDetailKv label="Drill rig" value={plannedMapSelection.row.drill_rig} />
                          <PlannedDetailKv label="Final depth (m)" value={plannedMapSelection.row.final_depth} />
                          <PlannedDetailKv label="Logged (m)" value={plannedMapSelection.row.logged_depth_m} />
                        </div>
                      ) : null}

                      {plannedMapSelection.type === 'planned' && plannedMapSelection.row ? (
                        <div>
                          <PlannedDetailKv label="Prop hole ID" value={plannedMapSelection.row.prop_hole_id} />
                          <PlannedDetailKv label="Hole ID" value={plannedMapSelection.row.hole_id} />
                          <PlannedDetailKv label="Status" value={plannedMapSelection.row.status} />
                          <PlannedDetailKv label="Program" value={plannedMapSelection.row.program} />
                          <PlannedDetailKv label="Phase" value={plannedMapSelection.row.phase_label} />
                          <PlannedDetailKv label="Prospect" value={plannedMapSelection.row.prospect} />
                          <PlannedDetailKv label="Target lens" value={plannedMapSelection.row.target_lens} />
                          <PlannedDetailKv label="Drill rig" value={plannedMapSelection.row.drill_rig} />
                          <PlannedDetailKv label="Rig number" value={plannedMapSelection.row.rig_number} />
                          <PlannedDetailKv label="Rig name" value={plannedMapSelection.row.rig_name} />
                          <PlannedDetailKv label="Pad" value={plannedMapSelection.row.pad_id} />
                          <PlannedDetailKv label="Tracking pad" value={plannedMapSelection.row.tracking_pad} />
                          <PlannedDetailKv label="Pad cleared" value={plannedMapSelection.row.drill_pad_cleared} />
                          <PlannedDetailKv label="Priority" value={plannedMapSelection.row.priority} />
                          <PlannedDetailKv label="Schedule" value={formatPlannedScheduleLabel(plannedMapSelection.row)} />
                          <PlannedDetailKv label="Days estimate" value={plannedMapSelection.row.days_estimate} />
                          {(plannedMapSelection.row.start_date || plannedMapSelection.row.end_date) && (
                            <PlannedDetailKv
                              label="Sheet start → end"
                              value={`${plannedMapSelection.row.start_date ?? ''}${plannedMapSelection.row.end_date ? ` → ${plannedMapSelection.row.end_date}` : ''}`}
                            />
                          )}
                          <PlannedDetailKv label="Estimated complete" value={plannedMapSelection.row.estimated_complete} />
                          <PlannedDetailKv label="EOH date" value={plannedMapSelection.row.eoh_date} />
                          <PlannedDetailKv label="Planned depth (m)" value={plannedMapSelection.row.planned_depth_m} />
                          <PlannedDetailKv label="Permit" value={plannedMapSelection.row.permit_number} />
                          <PlannedDetailKv label="Zone" value={plannedMapSelection.row.zone} />
                          <PlannedDetailKv label="Rehabilitation" value={plannedMapSelection.row.rehabilitation} />
                          <PlannedDetailKv label="Comments" value={plannedMapSelection.row.comments} />
                          <PlannedDetailKv label="Tracking notes" value={plannedMapSelection.row.tracking_notes} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </>
        )}

        {/* Tab Content - Coring Activity */}
        {activeTab === 'coring' && (
          <CoringActivity
            holeFilters={filteredData.map((h) => h.hole_id)}
            data={coringData}
            loading={coringLoading}
            error={coringError}
            dateRange={coringDateRange}
            setDateRange={setCoringDateRange}
            customStartDate={coringCustomStart}
            setCustomStartDate={setCoringCustomStart}
            customEndDate={coringCustomEnd}
            setCustomEndDate={setCoringCustomEnd}
            searchTerm={coringSearchTerm}
            setSearchTerm={setCoringSearchTerm}
          />
        )}

        {activeTab === 'coring-data' && (
          <CoringDataExplorer
            data={coringData}
            loading={coringLoading}
            error={coringError}
            dateParams={coringDateParams}
            dateRangeLabel={coringDateRange}
            setDateRange={setCoringDateRange}
            customStartDate={coringCustomStart}
            setCustomStartDate={setCoringCustomStart}
            customEndDate={coringCustomEnd}
            setCustomEndDate={setCoringCustomEnd}
            coringSearchTerm={coringSearchTerm}
            setSearchTerm={setCoringSearchTerm}
            holeFilters={filteredData.map((h) => h.hole_id)}
            filterContext={{
              statusFilters,
              filterType,
              zoneFilters,
              rigFilters,
              yearFilter,
              dashboardSearchTerm: searchTerm,
              selectedProjectId,
            }}
            filteredHoles={filteredData.map((h) => ({
              hole_id: h.hole_id,
              prospect: h.prospect,
              drill_rig: h.drill_rig,
              hole_status: h.hole_status
            }))}
          />
        )}

        {/* Drilling progress table vs core tray photos */}
        <Card variant="elevated" className="drilling-table-card full-width" style={{ padding: '1rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: bottomPanelSegment === 'program' ? '0.75rem' : '0.5rem',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#f9fafb' }}>
                  {bottomPanelSegment === 'core_photos' && selectedHoleId
                    ? (<IconText icon={ICON.uiCameraMap} iconSize={14}>{`Core photos — ${selectedHoleId}`}</IconText>)
                    : (<IconText icon={ICON.uiChartAnalytics} iconSize={14}>Drilling Progress Metrics</IconText>)}
                </h3>
                {bottomPanelSegment === 'program' && (
                  <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    {filteredData.length} of {campaignBaseData.length} holes
                  </span>
                )}
              </div>
              {selectedHoleId && (
                <div className="viz-filter-group" style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setBottomPanelSegment('program')}
                    className={`viz-filter-btn${bottomPanelSegment === 'program' ? ' active' : ''}`}
                  >
                    Program overview
                  </button>
                  <button
                    type="button"
                    onClick={() => setBottomPanelSegment('core_photos')}
                    className={`viz-filter-btn${bottomPanelSegment === 'core_photos' ? ' active' : ''}`}
                  >
                    Core photos
                  </button>
                </div>
              )}
            </div>
            {bottomPanelSegment === 'program' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search hole..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb',
                    fontSize: '0.875rem',
                    width: '220px',
                  }}
                />
                <div className="table-controls">
                  <Button variant="ghost" size="sm" onClick={exportToCSV}>
                        <IconText icon={ICON.uiExport} iconSize={16}>Export CSV</IconText>
                  </Button>
                  <select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      background: '#1f2937',
                      border: '1px solid #374151',
                      color: '#e5e7eb',
                      fontSize: '0.875rem',
                    }}
                  >
                    <option value="hole_id">Sort by Hole ID</option>
                    <option value="drilled_pct">Sort by Drilled %</option>
                    <option value="logged_pct">Sort by Logged %</option>
                    <option value="sampled_pct">Sort by Sampled %</option>
                    <option value="analysis_pct">Sort by Analysis %</option>
                  </select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <SelkirkIcon
                        name={sortDirection === 'asc' ? ICON.uiSortAsc : ICON.uiSortDesc}
                        size={14}
                        decorative
                      />
                      {sortDirection === 'asc' ? 'Asc' : 'Desc'}
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {bottomPanelSegment === 'program' && (
            <>
              <div
                style={{
                  padding: '0 1.5rem 1rem',
                  display: 'flex',
                  gap: '0.75rem',
                  borderBottom: '1px solid #374151',
                  flexWrap: 'wrap',
                }}
              >
                {['Complete', 'In Progress', 'Planned', 'Lost'].map((status) => (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: statusFilters[status] ? '#f9be0b' : '#374151',
                      background: statusFilters[status] ? 'rgba(249, 190, 11, 0.1)' : '#1f2937',
                      color: statusFilters[status] ? '#f9be0b' : '#6b7280',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: statusFilters[status] ? 1 : 0.5,
                    }}
                  >
                    {status === 'Complete' && (statusFilters[status] ? <SelkirkIcon name={ICON.uiCheckOn} size={14} decorative /> : <SelkirkIcon name={ICON.uiRadioOff} size={14} decorative />)}
                    {status === 'In Progress' && (statusFilters[status] ? <SelkirkIcon name={ICON.uiRefresh} size={14} decorative /> : <SelkirkIcon name={ICON.uiRadioOff} size={14} decorative />)}
                    {status === 'Planned' && (statusFilters[status] ? <SelkirkIcon name={ICON.uiCalendarDate} size={14} decorative /> : <SelkirkIcon name={ICON.uiRadioOff} size={14} decorative />)}
                    {status === 'Lost' && (statusFilters[status] ? <SelkirkIcon name={ICON.uiError} size={14} decorative /> : <SelkirkIcon name={ICON.uiRadioOff} size={14} decorative />)} {status}
                  </button>
                ))}
              </div>
              {filteredData.length > 0 ? (
                <div style={{ padding: '0 0.5rem' }}>
                  <Table
                    data={filteredData}
                    columns={drillingColumns}
                    hoverable
                    compact
                    onRowClick={(row) => {
                      setSelectedHoleId(row.hole_id);
                      setSelectedHole(row);
                      const mapElement = document.querySelector('.drill-hole-map-container');
                      if (mapElement) {
                        mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="no-data-message" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p>No drilling records match your filters.</p>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Try adjusting your search or status filter.
                  </p>
                </div>
              )}
            </>
          )}

          {bottomPanelSegment === 'core_photos' && selectedHoleId && (
            <CorePhotosGallery
              holeId={selectedHoleId}
              photos={corePhotosPhotos}
              loading={corePhotosLoading}
              errorMessage={corePhotosError}
            />
          )}
        </Card>

        </div> {/* End Main Content Area */}

      </div>
    </Layout>
  );
};

export default DrillingDashboard;
